import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock, CheckCircle, X } from "lucide-react";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { Logo } from "@/components/ui/Logo";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { useCallerCountry } from "@/hooks/useCallerCountry";

const COMPETITORS = [
  { name: "Lycamobile", expiry: "30 days", icon: X },
  { name: "Lebara", expiry: "30 days", icon: X },
  { name: "Skype Credit", expiry: "180 days", icon: X },
  { name: "Rebtel", expiry: "365 days", icon: X },
  { name: "elefa Connect", expiry: "Never", icon: CheckCircle, highlight: true },
];

export default function NoExpiry() {
  const { formatLocalAmount } = useCallerCountry();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={PAGE_SEO.noExpiry.title}
        description={PAGE_SEO.noExpiry.description}
        keywords={PAGE_SEO.noExpiry.keywords}
        canonicalUrl={getCanonicalUrl("/no-expiry")}
        structuredData={getBreadcrumbSchema([
          { name: "Home", url: "https://elefa.tel/" },
          { name: "Credit Never Expires", url: "https://elefa.tel/no-expiry" },
        ])}
      />

      <nav className="container py-4 sm:py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo variant="header" size="md" asLink={false} withIcon fullName />
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/connect-portal/auth"><Button variant="ghost" size="sm">Log In</Button></Link>
          <Link to="/connect-portal/auth"><Button size="sm">Get Started</Button></Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <Badge variant="secondary"><Clock className="h-3 w-3 mr-1 inline" /> No expiry on purchased credit</Badge>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight">
            Credit That<br />Never Expires
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Other calling apps expire your credit after 30 days. We think that's wrong. Top up once, use it whenever. Purchased credit on elefa does not expire. Promotional and referral credit may have different terms. See the relevant programme for details.
          </p>
          <Link to="/connect-portal/auth">
            <Button size="lg" className="gap-2 mt-4">Get started free <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 bg-muted/30 border-t">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10">How We Compare</h2>
          <div className="max-w-2xl mx-auto space-y-3">
            {COMPETITORS.map((c) => (
              <div
                key={c.name}
                className={`flex items-center justify-between px-6 py-4 rounded-lg border ${
                  c.highlight
                    ? "bg-primary/5 border-primary"
                    : "bg-background"
                }`}
              >
                <div className="flex items-center gap-3">
                  <c.icon className={`h-5 w-5 ${c.highlight ? "text-primary" : "text-destructive"}`} />
                  <span className={`font-medium ${c.highlight ? "text-primary" : ""}`}>{c.name}</span>
                </div>
                <span className={`font-semibold ${c.highlight ? "text-primary" : "text-muted-foreground"}`}>
                  {c.expiry}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 border-t">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <h2 className="text-3xl font-bold">Simple. Fair. Yours.</h2>
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-4xl font-bold text-primary">{formatLocalAmount(500)}</div>
                <p className="text-muted-foreground">Minimum top-up</p>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-primary">∞</div>
                <p className="text-muted-foreground">Days to use it</p>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-bold text-primary">{formatLocalAmount(0)}</div>
                <p className="text-muted-foreground">Monthly fees</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary/5 border-t">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Your Credit. Your Timeline.</h2>
          <p className="text-lg text-muted-foreground mb-8">Stop losing money to expiry dates. Switch to elefa Connect.</p>
          <Link to="/connect-portal/auth">
            <Button size="lg" className="gap-2">Get Started Free <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      <ConnectFooter />
    </div>
  );
}
